import { z } from 'zod';

export type PreferenceType = 'boolean' | 'checkbox' | 'select';
export type PreferenceCategory = 'personal' | 'sound';

export interface SelectOption {
  value: string;
  label: string;
}

export interface PreferenceConfigBase {
  key: string;
  label: string;
  description?: string;
  category: PreferenceCategory;
}

export interface BooleanPreferenceConfig extends PreferenceConfigBase {
  type: 'boolean' | 'checkbox';
  defaultValue: boolean;
}

export interface SelectPreferenceConfig extends PreferenceConfigBase {
  type: 'select';
  options: SelectOption[];
  defaultValue: string;
}

export type PreferenceConfig = BooleanPreferenceConfig | SelectPreferenceConfig;

export interface PreferencesState {
  [key: string]: boolean | string;
}

export const PREFERENCES_COOKIE_PREFIX = 'pierre_preferences';

/**
 * @deprecated Use getPreferencesCookieName(userId) instead
 */
export const PREFERENCES_COOKIE_NAME = 'pierre_preferences';

/**
 * Gets the cookie name for user-specific preferences.
 * If no userId is provided, returns the default cookie name.
 */
export function getPreferencesCookieName(userId?: string): string {
  if (!userId) {
    return PREFERENCES_COOKIE_PREFIX;
  }
  return `${PREFERENCES_COOKIE_PREFIX}_${userId}`;
}

export const CATEGORY_LABELS: Record<PreferenceCategory, string> = {
  personal: 'Pessoal',
  sound: 'Som',
};

// =============================================================================
// NEW: Namespaced Preferences Structure with Zod Validation
// =============================================================================

// Sound preferences schema
export const soundPreferencesSchema = z.object({
  enableSounds: z.boolean().default(true),
  enableVibration: z.boolean().default(true),
});

export type SoundPreferences = z.infer<typeof soundPreferencesSchema>;

// Personal preferences schema
export const personalPreferencesSchema = z.object({
  homePage: z.enum(['chat', 'dashboard']).default('dashboard'),
  colorScheme: z.enum(['light', 'dark', 'auto']).default('dark'),
});

export type PersonalPreferences = z.infer<typeof personalPreferencesSchema>;

// Complete namespaced preferences schema
export const namespacedPreferencesSchema = z.object({
  sound: soundPreferencesSchema.default({
    enableSounds: true,
    enableVibration: true,
  }),
  personal: personalPreferencesSchema.default({
    homePage: 'dashboard' as const,
    colorScheme: 'dark' as const,
  }),
});

export type NamespacedPreferences = z.infer<typeof namespacedPreferencesSchema>;

// Default values for namespaced preferences (versionado no código)
export const DEFAULT_NAMESPACED_PREFERENCES: NamespacedPreferences = {
  sound: {
    enableSounds: true,
    enableVibration: true,
  },
  personal: {
    homePage: 'dashboard',
    colorScheme: 'dark',
  },
};

// Partial overrides schema (for PATCH operations)
export const preferencesOverridesSchema = z.object({
  sound: soundPreferencesSchema.partial().optional(),
  personal: personalPreferencesSchema.partial().optional(),
});

export type PreferencesOverrides = z.infer<typeof preferencesOverridesSchema>;

/**
 * Deep merges defaults with user overrides.
 * Returns the effective preferences.
 */
export function mergePreferences(
  overrides: PreferencesOverrides = {},
): NamespacedPreferences {
  return {
    sound: {
      ...DEFAULT_NAMESPACED_PREFERENCES.sound,
      ...(overrides.sound || {}),
    },
    personal: {
      ...DEFAULT_NAMESPACED_PREFERENCES.personal,
      ...(overrides.personal || {}),
    },
  };
}

/**
 * Validates preference overrides data.
 * Returns the validated data or throws an error.
 */
export function validatePreferenceOverrides(
  data: unknown,
): PreferencesOverrides {
  return preferencesOverridesSchema.parse(data);
}

/**
 * Safely validates preference overrides data.
 * Returns null if validation fails.
 */
export function safeValidatePreferenceOverrides(
  data: unknown,
): PreferencesOverrides | null {
  const result = preferencesOverridesSchema.safeParse(data);
  if (result.success) {
    return result.data;
  }
  return null;
}

// =============================================================================
// LEGACY: Flat Preferences Configuration (for backward compatibility)
// =============================================================================

export const PREFERENCES_CONFIG: PreferenceConfig[] = [
  {
    key: 'homePage',
    label: 'Página inicial',
    description:
      'Define qual tela será exibida ao abrir o Pierre. O Chat permite conversar com o assistente financeiro, enquanto o Dashboard mostra um resumo visual das suas finanças.',
    type: 'select',
    category: 'personal',
    options: [
      { value: 'chat', label: 'Chat' },
      { value: 'dashboard', label: 'Dashboard' },
    ],
    defaultValue: 'dashboard',
  },
  {
    key: 'colorScheme',
    label: 'Aparência',
    description:
      'Define o esquema de cores do Pierre. O modo Escuro reduz o brilho da tela, o Claro usa cores claras, e o Auto segue as configurações do seu dispositivo.',
    type: 'select',
    category: 'personal',
    options: [
      { value: 'light', label: 'Claro' },
      { value: 'dark', label: 'Escuro' },
      { value: 'auto', label: 'Auto' },
    ],
    defaultValue: 'dark',
  },
  {
    key: 'enableSounds',
    label: 'Habilitar sons',
    description:
      'Reproduz efeitos sonoros ao receber notificações, alertas de transações e mensagens do assistente. Desative para usar o Pierre em modo silencioso.',
    type: 'boolean',
    category: 'sound',
    defaultValue: true,
  },
  {
    key: 'enableVibration',
    label: 'Habilitar vibrações',
    description:
      'Ativa o feedback tátil do dispositivo ao receber notificações e alertas. Útil para ser notificado discretamente mesmo com o som desativado.',
    type: 'boolean',
    category: 'sound',
    defaultValue: true,
  },
];

export function getDefaultPreferences(): PreferencesState {
  return PREFERENCES_CONFIG.reduce((acc, pref) => {
    acc[pref.key] = pref.defaultValue;
    return acc;
  }, {} as PreferencesState);
}

export function getPreferencesByCategory(
  category: PreferenceCategory,
): PreferenceConfig[] {
  return PREFERENCES_CONFIG.filter((pref) => pref.category === category);
}

export function getAllCategories(): PreferenceCategory[] {
  return [...new Set(PREFERENCES_CONFIG.map((pref) => pref.category))];
}

// =============================================================================
// MIGRATION HELPERS: Convert between flat and namespaced formats
// =============================================================================

/**
 * Converts flat preferences (from cookies) to namespaced format (for DB).
 */
export function flatToNamespaced(flat: PreferencesState): PreferencesOverrides {
  const overrides: PreferencesOverrides = {};

  // Map flat keys to namespaced structure
  if (flat.enableSounds !== undefined) {
    overrides.sound = overrides.sound || {};
    overrides.sound.enableSounds = flat.enableSounds as boolean;
  }
  if (flat.enableVibration !== undefined) {
    overrides.sound = overrides.sound || {};
    overrides.sound.enableVibration = flat.enableVibration as boolean;
  }
  if (flat.homePage !== undefined) {
    overrides.personal = overrides.personal || {};
    overrides.personal.homePage = flat.homePage as 'chat' | 'dashboard';
  }
  if (flat.colorScheme !== undefined) {
    overrides.personal = overrides.personal || {};
    overrides.personal.colorScheme = flat.colorScheme as
      | 'light'
      | 'dark'
      | 'auto';
  }

  return overrides;
}

/**
 * Converts namespaced preferences (from DB) to flat format (for cookies/UI).
 */
export function namespacedToFlat(
  namespaced: NamespacedPreferences,
): PreferencesState {
  return {
    enableSounds: namespaced.sound.enableSounds,
    enableVibration: namespaced.sound.enableVibration,
    homePage: namespaced.personal.homePage,
    colorScheme: namespaced.personal.colorScheme,
  };
}

/**
 * Gets only the overrides (differences from defaults) in namespaced format.
 * This is what gets stored in the database.
 */
export function getOverridesOnly(
  preferences: NamespacedPreferences,
): PreferencesOverrides {
  const overrides: PreferencesOverrides = {};

  // Sound overrides
  if (
    preferences.sound.enableSounds !==
    DEFAULT_NAMESPACED_PREFERENCES.sound.enableSounds
  ) {
    overrides.sound = overrides.sound || {};
    overrides.sound.enableSounds = preferences.sound.enableSounds;
  }
  if (
    preferences.sound.enableVibration !==
    DEFAULT_NAMESPACED_PREFERENCES.sound.enableVibration
  ) {
    overrides.sound = overrides.sound || {};
    overrides.sound.enableVibration = preferences.sound.enableVibration;
  }

  // Personal overrides
  if (
    preferences.personal.homePage !==
    DEFAULT_NAMESPACED_PREFERENCES.personal.homePage
  ) {
    overrides.personal = overrides.personal || {};
    overrides.personal.homePage = preferences.personal.homePage;
  }
  if (
    preferences.personal.colorScheme !==
    DEFAULT_NAMESPACED_PREFERENCES.personal.colorScheme
  ) {
    overrides.personal = overrides.personal || {};
    overrides.personal.colorScheme = preferences.personal.colorScheme;
  }

  return overrides;
}
